import styled from "@emotion/native"
import { useState, useEffect } from "react"
import { Spacing, Text, TextInput, ButtonV2, KeyboardAvoidingView } from "../../ui-components"
import { Platform } from "react-native"
import { PersonalDataResponse, usePersonalData } from "../../api/queries"
import { OnboardingScreenWrapper } from "./OnboardingScreenWrapper.tsx"
import { usePersonalDataMutation } from "../../api/mutations"
import { usePostHog } from "../../hooks"

type ProfileV2Props = {
  proceed?: (params: { firstName: string }) => void
}

export function ProfileV2UI({ proceed = () => null }: ProfileV2Props) {
  const { data } = usePersonalData()
  const { mutateAsync: updatePersonalData, isPending: isUpdating } = usePersonalDataMutation()
  const posthog = usePostHog()
  const [inputValues, setInputValues] = useState<PersonalDataResponse>({
    profileName: "",
    firstName: "",
    lastName: "",
    birthDate: "",
    ssnLastFourDigits: "",
    emailAddress: "",
    phoneNumber: "",
  })
  const [emailMissing, setEmailMissing] = useState(false)

  useEffect(() => {
    if (data) {
      setInputValues(data)
    }
  }, [data])

  const handleSetInputValue = (fieldName: keyof PersonalDataResponse) => {
    return function (newValue: string) {
      if (fieldName === "emailAddress") {
        setEmailMissing(false)
      }

      setInputValues((curr) => ({
        ...curr,
        [fieldName]: newValue,
      }))
    }
  }

  async function submit() {
    if (!inputValues.emailAddress) {
      setEmailMissing(true)
      return
    }
    await updatePersonalData({
      emailAddress: inputValues.emailAddress,
      profileName: "FakeProfileName" + Math.random(),
    })
    posthog.capture("$set", {
      $set: {
        email: inputValues.emailAddress,
        firstName: inputValues.firstName,
        lastName: inputValues.lastName,
      },
    })

    proceed({ firstName: inputValues.firstName })
  }

  return (
    <KeyboardAvoidingView keyboardVerticalOffset={0}>
      <OnboardingScreenWrapper stepNumber={5}>
        <Spacing top={52} />
        <Container contentContainerStyle={{ flexGrow: 1, justifyContent: "space-between" }}>
          <UpperContentWrapper>
            <Text type="h1">Your Profile</Text>
            <Spacing top={8} />
            <Text fontSize={20} lineHeight={32} fontFamily="IBMPlexSans-Regular" color="gray900">
              Confirm your full legal details
            </Text>
            <Spacing top={52} />
            <InputsWrapper>
              <TextInput labelText="First name" value={inputValues.firstName} editable={false} />
              <TextInput labelText="Last name" value={inputValues.lastName} editable={false} />
              <TextInput
                labelText="Email address"
                testID="emailAddressInput"
                value={inputValues.emailAddress}
                onChangeText={handleSetInputValue("emailAddress")}
                autoComplete="email"
                inputMode="email"
                keyboardType="email-address"
                autoCapitalize="none"
                style={emailMissing ? { borderWidth: 2, borderColor: "red" } : {}}
              />
            </InputsWrapper>
            <Spacing top={20} />
          </UpperContentWrapper>
          <Spacing left={20} right={20} bottom={24}>
            <ButtonV2
              title="Continue"
              testID="profileContinueButton"
              variant="blue2"
              onPress={submit}
              loading={isUpdating}
            />
          </Spacing>
          {Platform.OS !== "ios" && <Spacing top={20} />}
        </Container>
      </OnboardingScreenWrapper>
    </KeyboardAvoidingView>
  )
}

const Container = styled.ScrollView`
  flex: 1;
`

const InputsWrapper = styled.View`
  gap: 16px;
`

const UpperContentWrapper = styled.View`
  margin: 0 20px;
`
